import { useMemo } from 'react';
import { JSONValue, PanelDefinition, UnknownDefinition } from '@/core';
import { BasePanelProps, PanelChromeProps, VizComponentProps } from '@/plugin-system/model';
import {
  createPanelDefinitionFromVizPanel,
  useDataQueryResult,
  useExperimentalProcessQueryDefinitions,
} from '@/plugin-system/runtime';
import { CommonChartProps } from '@/plugin-system/utils';

export interface UseGenerateVizPanelDefinitionProps<
  ComponentProps extends BasePanelProps,
  DashboardSpec extends JSONValue,
> {
  /**
   * The kind of the chart. Should match the `DashboardPanel.kind` value in the definition for the visualization.
   */
  chartKind?: string;
  /**
   * Programmatic props for configuring the visualization.
   */
  chartProps: VizComponentProps<ComponentProps>;
  /**
   * Function that translates the programmatic props for a visualization into a dashboard spec for that visualization.
   *
   * Pass the `DashboardPanel.mapPropsToSpec` function from the definition for the visualization
   * (e.g. `StatChart.DashboardPanel.mapPropsToSpec` for the stat chart) for default handling. Pass your own function
   * for custom handling.
   */
  mapPropsToSpec?: (props: Omit<ComponentProps, CommonChartProps>) => DashboardSpec;
  /**
   * Props for the panel chrome that impact the rendering of the container around the visualization.
   */
  panelChromeProps?: PanelChromeProps;
}

export interface UseGenerateVizPanelDefinitionResponse<DashboardSpec extends JSONValue> {
  /**
   * Panel definition generated based on the provided information and context.
   */
  panelDefinition?: PanelDefinition<DashboardSpec>;
}

const FALLBACK_QUERIES: UnknownDefinition[] = [];

/**
 * Hook that takes information about a context-aware data visualization and attempts to translate it into a panel
 * definition.
 *
 * This hook depends on common viz framework context providers (e.g. `DataQueriesProvider`, `TemplateVariableProvider`)
 * and must be called inside them.
 *
 * This hook is used as an implementation detail in `.Viz` versions of visualizations to automatically generate panel
 * definitions and may be used alongside `VizContainer` and `.VizBase` versions of components to reproduce that
 * behavior.
 */
export const useGenerateVizPanelDefinition = <ComponentProps extends BasePanelProps, DashboardSpec extends JSONValue>({
  chartKind,
  chartProps,
  mapPropsToSpec,
  panelChromeProps = {},
}: UseGenerateVizPanelDefinitionProps<
  ComponentProps,
  DashboardSpec
>): UseGenerateVizPanelDefinitionResponse<DashboardSpec> => {
  let originalDefinitions: UnknownDefinition[] | undefined;
  try {
    const ctx = useDataQueryResult();
    originalDefinitions = ctx.queryResults
      .map((r) => r.definition?.spec.plugin)
      .filter((d) => d !== undefined) as UnknownDefinition[];
  } catch {
    // noop
  }

  const results = useExperimentalProcessQueryDefinitions(originalDefinitions ?? FALLBACK_QUERIES);

  const queries = useMemo(() => {
    if (originalDefinitions?.length === 0) {
      // Support empty queries to ensure this utility still works for viz that do not require queries
      // like the Markdown panel.
      return FALLBACK_QUERIES;
    }

    const processedQueries = results.data?.map((r) => r.definition);

    return processedQueries;
  }, [originalDefinitions?.length, results.data]);

  const panelDefinition = useMemo(() => {
    // if we don't have chartKind, queries and mapPropsToSpec, we can't determine the panelDefinition
    if (chartKind && queries && mapPropsToSpec) {
      const { links, ...otherChartProps } = chartProps;
      return createPanelDefinitionFromVizPanel({
        kind: chartKind,
        chartProps: otherChartProps as ComponentProps,
        queries,
        mapPropsToSpec,
        links,
        panelChromeProps,
      });
    }
  }, [chartKind, chartProps, mapPropsToSpec, panelChromeProps, queries]);

  return {
    panelDefinition,
  };
};
